import React, { useState } from "react";
import {
  vitaminsData,
  mineralsData,
  proteinSources,
  balancedDietByAge
} from "./data/nutritionData";
import "./NutritionLibrary.css";

function NutritionLibrary() {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");

  return (
    <div className="nutrition-library">

      <button
        className="library-toggle"
        onClick={() => setOpen(!open)}
      >
        📘 Nutrition Knowledge Library (পুষ্টি জ্ঞান ভান্ডার)
      </button>

      {open && (
        <div className="library-content">

          {/* ================= OVERVIEW ================= */}
          <Section
            title="1️⃣ Nutrition Fundamentals (পুষ্টির মৌলিক ধারণা)"
            id="overview"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <MacroOverview />
            <IndianThaliModel />
            <HydrationGuide />
            <BMIGuide />
          </Section>

          {/* ================= VITAMINS ================= */}
          <Section
            title="2️⃣ Vitamins (ভিটামিন)"
            id="vitamins"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <AdvancedTable
              headers={[
                "Name",
                "Daily Requirement",
                "Function",
                "Indian Sources",
                "Deficiency",
                "Excess Risk"
              ]}
              data={vitaminsData}
            />
          </Section>

          {/* ================= MINERALS ================= */}
          <Section
            title="3️⃣ Minerals (খনিজ পদার্থ)"
            id="minerals"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <AdvancedTable
              headers={[
                "Name",
                "Daily Requirement",
                "Function",
                "Indian Sources",
                "Deficiency"
              ]}
              data={mineralsData}
            />
          </Section>

          {/* ================= PROTEIN ================= */}
          <Section
            title="4️⃣ Protein Intelligence (প্রোটিন নির্দেশিকা)"
            id="protein"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <AdvancedTable
              headers={[
                "Food",
                "Protein Content",
                "Calories"
              ]}
              data={proteinSources}
            />
            <ProteinEducation />
          </Section>

          {/* ================= AGE WISE ================= */}
          <Section
            title="5️⃣ Balanced Diet By Age (বয়সভিত্তিক সুষম খাদ্য)"
            id="age"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <AdvancedTable
              headers={[
                "Age Group",
                "Calories",
                "Focus",
                "Protein Need",
                "Recommendation",
                "Sample Plate"
              ]}
              data={balancedDietByAge}
            />
          </Section>

          {/* ================= DISEASE PREVENTION ================= */}
          <Section
            title="6️⃣ Disease Prevention Nutrition"
            id="disease"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <DiseasePrevention />
          </Section>

          {/* ================= PORTION GUIDE ================= */}
          <Section
            title="7️⃣ Indian Portion Size Guide"
            id="portion"
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <PortionGuide />
          </Section>

        </div>
      )}
    </div>
  );
}

/* ======================================================
   SECTION WRAPPER
====================================================== */
function Section({ title, id, activeSection, setActiveSection, children }) {
  const isOpen = activeSection === id;

  return (
    <div className="section-wrapper">
      <div
        className="section-header"
        onClick={() => setActiveSection(isOpen ? "" : id)}
      >
        {title}
      </div>

      {isOpen && (
        <div className="section-body">
          {children}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   ADVANCED TABLE
====================================================== */
function AdvancedTable({ headers, data }) {
  return (
    <div className="table-wrapper">
      <table className="nutrition-table">
        <thead>
          <tr>
            {headers.map((head, i) => (
              <th key={i}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {Object.values(row).map((val, idx) => (
                <td key={idx}>{val}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ======================================================
   MACRO OVERVIEW
====================================================== */
function MacroOverview() {
  return (
    <div className="info-card">
      <h3>Macronutrient Distribution (ICMR Guidelines)</h3>
      <ul>
        <li>Carbohydrates: 50–60% of daily calories</li>
        <li>Protein: 10–20%</li>
        <li>Fat: 20–30%</li>
        <li>Fiber: 25–40g per day</li>
      </ul>
      <p>
        In a Bengali Indian context, a balanced plate includes:
        rice/roti + dal + sabji + protein + curd.
      </p>
    </div>
  );
}

/* ======================================================
   INDIAN THALI MODEL
====================================================== */
function IndianThaliModel() {
  return (
    <div className="info-card">
      <h3>Ideal Indian Thali Model</h3>
      <ul>
        <li>25% Whole grains (rice, roti)</li>
        <li>25% Protein (dal, fish, egg, paneer)</li>
        <li>40% Vegetables</li>
        <li>10% Fruits + Dairy</li>
      </ul>
    </div>
  );
}

/* ======================================================
   HYDRATION GUIDE
====================================================== */
function HydrationGuide() {
  return (
    <div className="info-card">
      <h3>Hydration Guide</h3>
      <ul>
        <li>Adults: 2.5–3 litres daily</li>
        <li>Summer: 3–3.5 litres</li>
        <li>Include coconut water, chaas, lemon water</li>
      </ul>
    </div>
  );
}

/* ======================================================
   BMI GUIDE
====================================================== */
function BMIGuide() {
  return (
    <div className="info-card">
      <h3>BMI Categories (Asian Standard)</h3>
      <ul>
        <li>Underweight: &lt;18.5</li>
        <li>Normal: 18.5–22.9</li>
        <li>Overweight: 23–24.9</li>
        <li>Obese: 25+</li>
      </ul>
    </div>
  );
}

/* ======================================================
   PROTEIN EDUCATION
====================================================== */
function ProteinEducation() {
  return (
    <div className="info-card">
      <h3>Protein Quality</h3>
      <p>
        Combine rice + dal for complete amino acid profile.
        Non-vegetarian sources provide complete protein directly.
      </p>
    </div>
  );
}

/* ======================================================
   DISEASE PREVENTION
====================================================== */
/* ======================================================
   DISEASE PREVENTION – ADVANCED INDIAN CONTEXT
====================================================== */
function DiseasePrevention() {
  return (
    <div className="info-card">

      <h3>🛡️ Clinical Nutrition for Disease Prevention (রোগ প্রতিরোধে পুষ্টি)</h3>

      <div className="disease-block">
        <h4>🩸 Iron Deficiency Anemia</h4>
        <p>
          Most common in Indian women and adolescents. Caused by low iron intake,
          poor absorption, or chronic blood loss.
        </p>
        <ul>
          <li><strong>Key Nutrients:</strong> Iron + Vitamin C + Folate + B12</li>
          <li><strong>Best Indian Sources:</strong> Palong shaak, masoor dal, jaggery, liver, egg yolk</li>
          <li><strong>Absorption Tip:</strong> Add lemon (Vitamin C) to dal or greens</li>
          <li><strong>Avoid:</strong> Tea/coffee within 1 hour of iron-rich meal</li>
          <li><strong>Portion Guide:</strong> 1 katori dal + 1 katori leafy greens daily</li>
        </ul>
      </div>

      <div className="disease-block">
        <h4>🩺 Type 2 Diabetes</h4>
        <p>
          High refined carbohydrate intake and sedentary lifestyle are major contributors.
        </p>
        <ul>
          <li><strong>Key Focus:</strong> Low Glycemic Index (GI) foods</li>
          <li><strong>Recommended:</strong> Brown/red rice, oats, whole wheat roti, dal, fish</li>
          <li><strong>Increase:</strong> Fiber (25–40g/day)</li>
          <li><strong>Limit:</strong> White rice excess, sweets, sugary drinks</li>
          <li><strong>Portion Rule:</strong> Rice not more than 1 cup cooked per meal</li>
        </ul>
      </div>

      <div className="disease-block">
        <h4>❤️ Hypertension (High Blood Pressure)</h4>
        <p>
          High salt intake and low potassium diet common in urban India.
        </p>
        <ul>
          <li><strong>Salt Limit:</strong> &lt;5g per day (1 teaspoon total)</li>
          <li><strong>Increase:</strong> Potassium-rich foods (banana, coconut water)</li>
          <li><strong>Include:</strong> Garlic, leafy greens, fish</li>
          <li><strong>Avoid:</strong> Processed snacks, pickles excess</li>
          <li><strong>Healthy Plate:</strong> 40% vegetables</li>
        </ul>
      </div>

      <div className="disease-block">
        <h4>🧈 NAFLD (Fatty Liver Disease)</h4>
        <p>
          Associated with excess sugar, obesity, and low physical activity.
        </p>
        <ul>
          <li><strong>Reduce:</strong> Sugar, refined flour, fried foods</li>
          <li><strong>Increase:</strong> Lean protein (fish, dal, egg)</li>
          <li><strong>Add:</strong> Omega-3 rich fish (rohu, hilsa occasionally)</li>
          <li><strong>Exercise:</strong> 30 min brisk walk daily</li>
        </ul>
      </div>

      <div className="disease-block">
        <h4>🦴 Osteoporosis (Bone Weakness)</h4>
        <ul>
          <li><strong>Key Nutrients:</strong> Calcium + Vitamin D + Magnesium</li>
          <li><strong>Sources:</strong> Milk, curd, sesame seeds, sunlight exposure</li>
          <li><strong>Daily Need:</strong> 1000–1200 mg calcium</li>
        </ul>
      </div>

      <div className="disease-block">
        <h4>⚖️ Obesity Prevention</h4>
        <ul>
          <li>Maintain calorie balance</li>
          <li>Protein 1g/kg body weight</li>
          <li>Avoid liquid calories</li>
          <li>Practice portion control</li>
        </ul>
      </div>

      <p style={{ marginTop: "15px", fontWeight: "500" }}>
        💡 Prevention through balanced diet is more effective than medication later.
      </p>

    </div>
  );
}

/* ======================================================
   PORTION GUIDE
====================================================== */
/* ======================================================
   PORTION GUIDE – INDIAN PRACTICAL MEASUREMENTS
====================================================== */
function PortionGuide() {
  return (
    <div className="info-card">

      <h3>🍽️ Indian Portion Size Guide (ভারতীয় পরিমাপ নির্দেশিকা)</h3>

      <p>
        Proper portion control helps prevent obesity, diabetes, and heart disease.
        Below are common household measures used in Indian kitchens.
      </p>

      <div className="portion-grid">

        <div>
          <h4>🍚 Grains</h4>
          <ul>
            <li>1 katori rice ≈ 150g cooked ≈ 180 kcal</li>
            <li>1 roti ≈ 30g flour ≈ 100 kcal</li>
            <li>1 cup oats ≈ 40g raw</li>
          </ul>
        </div>

        <div>
          <h4>🥣 Pulses & Dal</h4>
          <ul>
            <li>1 katori dal ≈ 100g cooked</li>
            <li>Protein ≈ 7–9g per serving</li>
          </ul>
        </div>

        <div>
          <h4>🥛 Dairy</h4>
          <ul>
            <li>1 cup milk ≈ 240ml</li>
            <li>Paneer (50g) ≈ 9g protein</li>
          </ul>
        </div>

        <div>
          <h4>🥚 Protein</h4>
          <ul>
            <li>1 egg ≈ 6g protein</li>
            <li>100g fish ≈ 20g protein</li>
            <li>100g chicken ≈ 27g protein</li>
          </ul>
        </div>

        <div>
          <h4>🥗 Vegetables</h4>
          <ul>
            <li>1 katori cooked sabji ≈ 80–100g</li>
            <li>At least 2–3 servings daily</li>
          </ul>
        </div>

        <div>
          <h4>🍎 Fruits</h4>
          <ul>
            <li>1 medium apple ≈ 150g</li>
            <li>1 banana ≈ 100 kcal</li>
          </ul>
        </div>

      </div>

      <div style={{ marginTop: "20px" }}>
        <h4>🧠 Simple Plate Rule</h4>
        <ul>
          <li>Half plate vegetables</li>
          <li>Quarter plate protein</li>
          <li>Quarter plate grains</li>
        </ul>
      </div>

      <p style={{ marginTop: "15px", fontWeight: "500" }}>
        ⚠️ Even healthy food in excess leads to weight gain. Portion awareness is key.
      </p>

    </div>
  );
}

export default NutritionLibrary;